import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { DemoRequestsTab } from "../components/DemoRequestsTab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { ArrowLeft, Trash2, Plus } from "lucide-react";

const ROLE_LABELS = { superadmin: "Superadmin", consultant: "Consultant", org_admin: "Organisatie-admin", viewer: "Viewer", member: "Medewerker" };

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [scopeOrg, setScopeOrg] = useState("");

  const isSuper = user.role === "superadmin";
  const isConsultant = user.role === "consultant";

  const loadOrgs = useCallback(() => api.get("/organizations").then(({ data }) => setOrgs(data)), []);
  const loadUsers = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}), []);

  useEffect(() => {
    loadOrgs();
    loadUsers();
  }, [loadOrgs, loadUsers]);

  useEffect(() => {
    if (scopeOrg) api.get(`/organizations/${scopeOrg}/employees`).then(({ data }) => setEmployees(data));
  }, [scopeOrg]);

  // ---------- organizations ----------
  const [orgName, setOrgName] = useState("");
  const createOrg = async () => {
    if (!orgName.trim()) return;
    try {
      await api.post("/organizations", { name: orgName });
      setOrgName("");
      loadOrgs();
      toast.success("Organisatie aangemaakt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const deleteOrg = async (id) => {
    if (!window.confirm("Organisatie en al het model verwijderen?")) return;
    await api.delete(`/organizations/${id}`);
    loadOrgs();
    toast.success("Verwijderd");
  };

  // ---------- users ----------
  const emptyUser = { email: "", password: "", name: "", role: isSuper ? "consultant" : "member", organization_id: "", employee_id: "" };
  const [userForm, setUserForm] = useState(emptyUser);
  const [userDialog, setUserDialog] = useState(false);

  const roleOptions = isSuper
    ? ["consultant", "org_admin", "viewer", "member"]
    : ["org_admin", "viewer", "member"];

  const createUser = async () => {
    try {
      const body = { ...userForm };
      if (body.role === "consultant") {
        delete body.organization_id;
        delete body.employee_id;
      }
      if (!body.employee_id) delete body.employee_id;
      await api.post("/users", body);
      setUserDialog(false);
      setUserForm(emptyUser);
      loadUsers();
      toast.success("Gebruiker aangemaakt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const deleteUser = async (id) => {
    if (!window.confirm("Gebruiker verwijderen?")) return;
    await api.delete(`/users/${id}`);
    loadUsers();
    toast.success("Verwijderd");
  };

  // load employees for the org chosen in the user form (for linking a member)
  const [formOrgEmployees, setFormOrgEmployees] = useState([]);
  useEffect(() => {
    if (userForm.organization_id) api.get(`/organizations/${userForm.organization_id}/employees`).then(({ data }) => setFormOrgEmployees(data));
    else setFormOrgEmployees([]);
  }, [userForm.organization_id]);

  const orgName2 = (id) => orgs.find((o) => o.id === id)?.name || "—";

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 bg-white border-b border-zinc-900 flex items-center justify-between px-5 h-16">
        <div className="flex items-center gap-4">
          <Button variant="ghost" className="rounded-none" onClick={() => navigate("/")} data-testid="back-to-modeler-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> Terug naar model
          </Button>
          <div className="font-grotesk font-bold tracking-tight">BEHEER</div>
        </div>
        <div className="text-sm text-zinc-500">
          {user.name} · {ROLE_LABELS[user.role]}
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6">
        <Tabs defaultValue="orgs" data-testid="admin-nav-tabs">
          <TabsList className="rounded-none">
            <TabsTrigger value="orgs" className="rounded-none" data-testid="tab-orgs">Organisaties</TabsTrigger>
            <TabsTrigger value="users" className="rounded-none" data-testid="tab-users">Gebruikers</TabsTrigger>
            {isSuper && <TabsTrigger value="demos" className="rounded-none" data-testid="tab-demos">Demo-aanvragen</TabsTrigger>}
          </TabsList>

          {isSuper && (
            <TabsContent value="demos" className="mt-6">
              <h2 className="font-grotesk text-xl font-semibold tracking-tight mb-4">Demo-aanvragen</h2>
              <DemoRequestsTab />
            </TabsContent>
          )}

          {/* Organizations */}
          <TabsContent value="orgs" className="mt-6">
            {(isSuper || isConsultant) && (
              <div className="flex gap-2 mb-6">
                <Input placeholder="Naam nieuwe organisatie" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="rounded-none border-zinc-900 max-w-sm" data-testid="new-org-name-input" />
                <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={createOrg} data-testid="create-org-btn">
                  <Plus className="h-4 w-4 mr-1" /> Aanmaken
                </Button>
              </div>
            )}
            <div className="border border-zinc-900" data-testid="org-table">
              <div className="grid grid-cols-[1fr_auto] gap-4 px-4 py-2 border-b border-zinc-900 bg-zinc-100 text-xs uppercase tracking-widest font-semibold">
                <div>Organisatie</div>
                <div>Actie</div>
              </div>
              {orgs.map((o) => (
                <div key={o.id} className="grid grid-cols-[1fr_auto] gap-4 px-4 py-3 border-b border-zinc-200 items-center" data-testid="org-row">
                  <div className="font-grotesk font-medium">{o.name}</div>
                  {(isSuper || isConsultant) && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => deleteOrg(o.id)} data-testid="delete-org-button">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {orgs.length === 0 && <div className="px-4 py-6 text-sm text-zinc-400">Nog geen organisaties.</div>}
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-grotesk text-xl font-semibold tracking-tight">Gebruikers</h2>
              <Dialog open={userDialog} onOpenChange={setUserDialog}>
                <DialogTrigger asChild>
                  <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" data-testid="new-user-button">
                    <Plus className="h-4 w-4 mr-1" /> Nieuwe gebruiker
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="new-user-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-grotesk">Nieuwe gebruiker</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Naam</Label>
                      <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} className="rounded-none border-zinc-900 mt-1" data-testid="user-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">E-mail</Label>
                      <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className="rounded-none border-zinc-900 mt-1" data-testid="user-email-input" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Wachtwoord</Label>
                      <Input type="text" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} className="rounded-none border-zinc-900 mt-1" data-testid="user-password-input" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Rol</Label>
                      <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                        <SelectTrigger className="rounded-none border-zinc-900 mt-1" data-testid="user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roleOptions.map((r) => (
                            <SelectItem key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {userForm.role !== "consultant" && (
                      <div>
                        <Label className="text-xs uppercase tracking-widest">Organisatie</Label>
                        <Select value={userForm.organization_id} onValueChange={(v) => setUserForm({ ...userForm, organization_id: v })}>
                          <SelectTrigger className="rounded-none border-zinc-900 mt-1" data-testid="user-org-select">
                            <SelectValue placeholder="Kies organisatie" />
                          </SelectTrigger>
                          <SelectContent>
                            {orgs.map((o) => (
                              <SelectItem key={o.id} value={o.id}>
                                {o.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {userForm.role === "member" && formOrgEmployees.length > 0 && (
                      <div>
                        <Label className="text-xs uppercase tracking-widest">Koppel aan medewerker (optioneel)</Label>
                        <Select value={userForm.employee_id} onValueChange={(v) => setUserForm({ ...userForm, employee_id: v })}>
                          <SelectTrigger className="rounded-none border-zinc-900 mt-1" data-testid="user-employee-select">
                            <SelectValue placeholder="Geen koppeling" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOrgEmployees.map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.name} {e.role ? `· ${e.role}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={createUser} data-testid="create-user-button">
                      Aanmaken
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-zinc-900" data-testid="user-table">
              <div className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-4 px-4 py-2 border-b border-zinc-900 bg-zinc-100 text-xs uppercase tracking-widest font-semibold">
                <div>Naam / e-mail</div>
                <div>Rol</div>
                <div>Organisatie</div>
                <div>Actie</div>
              </div>
              {users.map((u) => (
                <div key={u.id} className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-4 px-4 py-3 border-b border-zinc-200 items-center" data-testid="user-row">
                  <div className="min-w-0">
                    <div className="font-grotesk font-medium truncate">{u.name}</div>
                    <div className="text-xs font-mono text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <div className="text-sm">{ROLE_LABELS[u.role]}</div>
                  <div className="text-sm truncate">{u.role === "consultant" ? "—" : orgName2(u.organization_id)}</div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => deleteUser(u.id)} data-testid="delete-user-button">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {users.length === 0 && <div className="px-4 py-6 text-sm text-zinc-400">Nog geen gebruikers.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
